import { useSelector } from "react-redux";
import { selectCartHasEntries, selectIsCartLoading } from "~/features/cart/cart/redux/selectors/cartSelectors";
import type { PageTemplateProps } from "./pageTemplateTypes";

/**
 * React translation of the old {@code pages/cart/cartPage.jsp}.
 *
 * The JSP renders the {@code TopContent} slot as the body-bearing include
 * ({@code CartComponent} / {@code cartDisplay.jsp}); per the 0153 body-replacement
 * decision that position becomes the route body here. The remaining CMS slot
 * regions are conditional on cart emptiness, mirroring the JSP
 * {@code c:if test="${not empty cartData.rootGroups}"} branches:
 *
 * - non-empty cart: {@code CenterLeftContentSlot}, {@code CenterRightContentSlot},
 *   {@code BottomContentSlot};
 * - empty cart: {@code EmptyCartMiddleContent} (wrapped in the legacy
 *   {@code content__empty} class).
 *
 * The route body ({@code createCartPage}) already branches empty/non-empty
 * content internally; these conditional slots are the ADDITIONAL CMS slot regions
 * from the JSP and are recreated here for structure-copy parity. A post-migration
 * PROD slot audit determines whether they actually carry content.
 *
 * {@code <cart:cartValidation/>} from the JSP is intentionally ignored (linked
 * usage audit); it disappears by virtue of deleting the JSP.
 *
 * The conditional slots render only once the cart-page load is not in flight, so
 * empty-cart slot content does not flash while the body spinner is showing.
 * {@code selectCartHasEntries} is the named feature selector equivalent to the
 * JSP {@code not empty cartData.rootGroups} condition.
 */
export function CartPageTemplate({ slots, body }: PageTemplateProps) {
  const isLoading = useSelector(selectIsCartLoading);
  const hasEntries = useSelector(selectCartHasEntries);
  const showNonEmpty = !isLoading && hasEntries;
  const showEmpty = !isLoading && !hasEntries;

  return (
    <>
      <div>
        <div>{body}</div>
      </div>
      {showNonEmpty && (
        <>
          {slots.renderSlot("CenterLeftContentSlot")}
          {slots.renderSlot("CenterRightContentSlot")}
          {slots.renderSlot("BottomContentSlot")}
        </>
      )}
      {showEmpty && <div className="content__empty">{slots.renderSlot("EmptyCartMiddleContent")}</div>}
    </>
  );
}
